import { $roles, getTag, useApiClient, useAsyncCache, useTranslation } from '@msdk/common'
import { PagedList } from '@msdk/definitions'
import { useI18nContext } from '@msdk/forms'
import { loadInvolvedMSForRoles, useRoleLabel, useSortedRoles } from '@msdk/functional-components'
import { Box, Divider, ListItemIcon, ListItemText, MenuItem, styled, Typography } from '@msdk/ui'
import { ChevronLeft } from '@mui/icons-material'
import { useEffect, useState } from 'react'
import { useRecoilValue } from 'recoil'

const StyledRolesBackMenuItem = styled(MenuItem)`
  margin-top: 16px;
  padding-top: 8px;
  padding-bottom: 8px;
  display: flex;
  justify-content: flex-start;
`

const StyledRolesBackLabel = styled(Typography)`
  font-weight: 700;
`

const StyledRoleTypography = styled(Typography)`
  overflow: hidden;
  white-space: initial;
`

const StyledRoleMenuItem = styled(MenuItem)`
  :active,
  :hover {
    background-color: inherit;
    cursor: default;
  }
`

interface Props {
  setUserRolesMenu: (toggle: boolean) => void
}

export function UserRoles({ setUserRolesMenu }: Props) {
  const t = useTranslation()
  const [userRoles, setUserRoles] = useState<string[]>([])
  const client = useApiClient()
  const cache = useAsyncCache()
  const getRoleLabel = useRoleLabel()
  const getSortedRoles = useSortedRoles()
  const i18n = useI18nContext()
  const roles = useRecoilValue($roles)

  useEffect(() => {
    const fetchData = async () => {
      const res = (await cache.getOrCreate('user-roles', () =>
        client.read<PagedList>('USM/api/RoleClaims/GetRolesForTenant'),
      )) as PagedList

      await loadInvolvedMSForRoles(res.source, i18n)

      const toExclude = 'app.user'

      const filteredRes = res.source.filter((e) =>
        roles.filter((r) => r != toExclude).includes(e.fields['NAME']?.value),
      )

      const sortedRes = getSortedRoles(filteredRes)

      setUserRoles(sortedRes.map(getRoleLabel))
    }

    fetchData().catch(console.error)
  }, [])

  return (
    <Box>
      <StyledRolesBackMenuItem
        className="roles-menu-item"
        onClick={() => setUserRolesMenu(false)}
        data-test-id={getTag('shell', 'user_roles', 'USER_ROLES', 'button')}
      >
        <ListItemIcon>
          <ChevronLeft />
        </ListItemIcon>
        <StyledRolesBackLabel>{t('LBL_MY_ROLES', 'My Roles')}</StyledRolesBackLabel>
      </StyledRolesBackMenuItem>
      <Divider />
      {userRoles.map((role) => (
        <StyledRoleMenuItem key={role} data-test-id={getTag('shell', 'user_roles', role, 'button')}>
          <ListItemText>
            <StyledRoleTypography onClick={undefined} variant="body1">
              {role}
            </StyledRoleTypography>
          </ListItemText>
        </StyledRoleMenuItem>
      ))}
    </Box>
  )
}
